"use client";

import React from "react";
import { UserCheck, Compass, TrendingUp, Clock, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
interface BenefitItem {
  id: string;
  title: string;
  badge: string;
  iconName: string;
  description: React.ReactNode;
}
const benefits: BenefitItem[] = [{
  id: "strategic-alignment",
  title: "Strategic Alignment & Accountability",
  badge: "Direct Accountability",
  iconName: "UserCheck",
  description: <>
        When you{" "}
        <Link to="/HireSeoConsultantPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium">
          hire an SEO consultant directly
        </Link>
        , you get complete strategic continuity and single-point accountability across your national campaign. I personally oversee every technical roadmap and content priority, ensuring direct alignment with your commercial growth.
      </>
}, {
  id: "tailored-strategy",
  title: "Tailored Custom Strategy",
  badge: "Bespoke Roadmaps",
  iconName: "Compass",
  description: "Every business has unique technical parameters and market opportunities. I engineer custom SEO roadmaps designed specifically around your brand's growth targets rather than relying on generic templates or automated checklists."
}, {
  id: "measurable-results",
  title: "Measurable Commercial Impact",
  badge: "Revenue-Focused SEO",
  iconName: "TrendingUp",
  description: <>
        Rankings are only valuable when they drive actual commercial performance. As an experienced{" "}
        <Link to="/SeoConsultantUkPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium">
          SEO consultant across the UK
        </Link>
        , my focus remains strictly on securing high-intent visibility, qualified inbound leads, and sustainable organic revenue.
      </>
}, {
  id: "ongoing-support",
  title: "Agile & Proactive Execution",
  badge: "Rapid Adaptation",
  iconName: "Clock",
  description: "Search engine algorithms and AI discovery models evolve continuously. Having direct line access to senior expertise allows your strategy to adapt instantly to new search developments and emerging opportunities."
}];
export default function Benefits() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  const renderIcon = (iconName: string) => {
    switch (iconName) {
      case "UserCheck":
        return <UserCheck className="size-6 text-primary" />;
      case "Compass":
        return <Compass className="size-6 text-primary" />;
      case "TrendingUp":
        return <TrendingUp className="size-6 text-primary" />;
      case "Clock":
        return <Clock className="size-6 text-primary" />;
      default:
        return <UserCheck className="size-6 text-primary" />;
    }
  };
  return <section id="consultantbenefits" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden isolate">
      <div className="absolute top-0 right-0 -z-10 size-96 rounded-full bg-secondary/30 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 -z-10 size-96 rounded-full bg-primary/5 blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-16 lg:mb-20">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs uppercase tracking-wider font-semibold">
            The Independent Consultant Model
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-default tracking-tight mb-6 text-foreground">
            Why Partner Directly With Me As Your SEO Consultant
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed font-default">
            Partnering directly with me connects senior strategic vision directly to execution, giving your business seamless strategic alignment and full personal accountability. From building resilient{" "}
            <Link to="/TechnicalSeoPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium">
              technical SEO foundations
            </Link>{" "}
            to conducting{" "}
            <Link to="/SeoAuditsPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium">
              a detailed SEO audit
            </Link>
            , every initiative is personally steered to maximize qualified national search visibility and organic revenue.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
          {benefits.map((benefit, i) => <motion.div key={benefit.id} variants={itemVariants} data-index={i}>
              <Card className="h-full border border-border/80 bg-card hover:border-primary/40 transition-[color,background-color,border-color,box-shadow] duration-300 shadow-sm hover:shadow-md flex flex-col justify-between">
                <div>
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between gap-4 mb-3">
                      <div className="p-3 rounded-lg bg-secondary/60 inline-flex items-center justify-center">
                        {renderIcon(benefit.iconName)}
                      </div>
                      <Badge variant="outline" className="text-xs font-medium border-border">
                        {benefit.badge}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl sm:text-2xl font-bold font-default text-card-foreground">
                      {benefit.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="text-muted-foreground leading-relaxed text-base font-default">
                      {benefit.description}
                    </p>
                  </CardContent>
                </div>
              </Card>
            </motion.div>)}
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.3
      }} className="mt-16 lg:mt-20 rounded-2xl bg-primary text-primary-foreground p-8 sm:p-10 lg:p-12 shadow-xl">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="space-y-4 text-center lg:text-left max-w-2xl">
              <div className="inline-flex items-center gap-2 text-primary-foreground/90 font-medium text-sm">
                <CheckCircle2 className="size-4 text-accent" />
                <span>Dedicated Personal Attention Guaranteed</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold font-default tracking-tight">
                Ready for an SEO Strategy Tailored Specifically for Your Business?
              </h3>
              <p className="text-primary-foreground/80 leading-relaxed text-base font-default">
                Book a free initial consultation to discuss your search goals directly with me. No sales pitch—just clear, actionable SEO strategy.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4 shrink-0 w-full lg:w-auto">
              <Button size="lg" variant="secondary" asChild className="w-full sm:w-auto font-semibold">
                <Link to="/ContactPage">
                  Book a Free Consultation
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild className="w-full sm:w-auto bg-transparent border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground">
                <Link to="/CaseStudiesPage">
                  Explore My Case Studies
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>

        <div className="mt-16 text-center border-t border-border/80 pt-12">
          <h3 className="text-lg sm:text-xl font-semibold font-default text-foreground mb-6">
            Explore Related SEO Services
          </h3>
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4 text-sm sm:text-base">
            <Link to="/LocalSEOPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium inline-flex items-center gap-1.5">
              <ArrowRight className="size-3.5" />
              Local SEO strategy
            </Link>
            <Link to="/B2BSeoLeadGenerationPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium inline-flex items-center gap-1.5">
              <ArrowRight className="size-3.5" />
              B2B lead generation SEO
            </Link>
            <Link to="/TechnicalSeoPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium inline-flex items-center gap-1.5">
              <ArrowRight className="size-3.5" />
              Technical SEO support
            </Link>
            <Link to="/SeoAuditsPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium inline-flex items-center gap-1.5">
              <ArrowRight className="size-3.5" />
              Comprehensive SEO audits
            </Link>
          </div>
        </div>
      </div>
    </section>;
}